import { DetailedHTMLProps, InputHTMLAttributes } from "react";

import _get from "lodash/get";
import { useRouter } from "next/router";
import { RegisterOptions, useFormContext } from "react-hook-form";

import { ChooseImageSchemaField } from "types/schema";

import Upsell from "../upsell/Upsell";

import styles from "./ChooseImage.module.scss";

export type ChooseImageProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "defaultChecked" | "value" | "className" | "type"
> & {
  aspectRatio?: string;
  desktopColumns?: number;
  mobileColumns?: number;
  name: string;
  options: ChooseImageSchemaField["metas"][0]["images"];
  registerOptions: RegisterOptions;
  upsells?: ChooseImageSchemaField["metas"][0]["upsells"];
};

export default function ChooseImage({
  aspectRatio = "1 / 1",
  mobileColumns = 2,
  desktopColumns = mobileColumns,
  name,
  options,
  registerOptions,
  upsells,
  ...inputProps
}: ChooseImageProps) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const router = useRouter();

  // Don't allow any changes to ChooseImage fields with upsells when editing an order
  const isDisabled = !!(upsells && router.query.order_id);

  return (
    <div
      aria-invalid={!!(errors && _get(errors, name))}
      aria-label={name}
      className={styles.group}
      role="radiogroup"
      style={
        {
          "--desktop-columns": desktopColumns,
          "--mobile-columns": mobileColumns,
        } as any
      }
    >
      {Object.entries(options).map(([key, { background, label }]) => (
        <div key={key} className={styles.option}>
          <input
            aria-label={label || key}
            className={styles.radio}
            disabled={isDisabled}
            id={`${name}_${key}`}
            type="radio"
            value={key}
            onFocus={(e) => {
              /**
               * iOS Safari doesn't scroll radio inputs into view, so we trigger it manually.
               * Luckily it's only been an issue for half a decade.
               *
               * https://stackoverflow.com/questions/56137175/ios-safari-chrome-wont-scroll-up-to-show-validation-error-message-on-radio-inpu/67215758#67215758
               */
              if (navigator.platform === "iPhone") {
                e.target.scrollIntoView({
                  block: "center",
                  behavior: "smooth",
                });
              }
            }}
            {...inputProps}
            {...register(name, registerOptions)}
          />

          <label className={styles.label} htmlFor={`${name}_${key}`}>
            {upsells && (
              <Upsell
                amount={
                  upsells.find((u) => u.id === key)?.price.formatted || "0.00"
                }
              />
            )}

            <img
              alt={key}
              className={styles.image}
              src={background}
              style={{ aspectRatio }}
            />

            {label && <p className={styles.imageLabel}>{label}</p>}
          </label>
        </div>
      ))}
    </div>
  );
}
